import React, { createRef, memo, useCallback, useEffect, useState } from 'react';
import { ArrowRightCircleFill, Display, Search } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';

import { Modal as AntdModal, ModalProps, Space, Typography, InputRef } from 'antd';

import { useDebounce } from 'usehooks-ts';

import EmptyState from '@/components/shared/EmptyState';
import InfiniteScroll from '@/components/shared/InfiniteScroll';
import SearchInput from '@/components/shared/SearchInput';
import { IMyCourse } from '@/interfaces/course';
import { IPaginationParams, IPaginationResponse } from '@/interfaces/pagination';
import searchApi from '@/services/search';
import styled, { css } from '@/styled';

interface ISearchModal {
  handlePressedAccessKey: (open: boolean) => void;
  closeModal: () => void;
}

function SearchModal({ className, open, handlePressedAccessKey, closeModal }: ModalProps & ISearchModal) {
  const [search, setSearch] = useState<string | null>(null);
  const [data, setData] = useState<IPaginationResponse<IMyCourse[]>>();
  const [loading, setLoading] = useState(false);
  const inputRef = createRef<InputRef>();
  const navigate = useNavigate();

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value || null);

  const debouncedSearch = useDebounce(search, 600);

  const onClickResult = (courseHash: string) => () => {
    closeModal();
    setSearch(null);
    navigate('/curso/' + courseHash);
  };

  const onPressAccessKey = useCallback(
    (e: KeyboardEvent) => {
      if ((e.ctrlKey && e.key == 'k') || (e.metaKey && e.key == 'k')) {
        e.preventDefault();
        handlePressedAccessKey(true);
      }
    },
    [handlePressedAccessKey]
  );

  const fetchSearch = useCallback((params: Partial<IPaginationParams>) => {
    setLoading(true);
    return searchApi(params)
      .then(response => response.data)
      .finally(() => setLoading(false));
  }, []);

  const handleReachedEnd = () => {
    if (data.total_pages <= data.page.page) return;
    fetchSearch({ search: debouncedSearch, page: data.page.page + 1 }).then(response => {
      setData({ ...response, data: [...data.data, ...response.data] });
    });
  };

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 150);
  }, [inputRef, open]);

  useEffect(() => {
    window.addEventListener('keydown', onPressAccessKey);
    return () => window.removeEventListener('keydown', onPressAccessKey);
  }, [onPressAccessKey]);

  useEffect(() => {
    if (debouncedSearch === null) return;
    fetchSearch({ search: debouncedSearch, page: 1 }).then(setData);
  }, [debouncedSearch, fetchSearch]);

  useEffect(() => {
    window.addEventListener('popstate', closeModal);
    return () => window.removeEventListener('popstate', closeModal);
  }, [closeModal]);

  const shouldRenderEmptyState = (data?.data ?? []).length === 0 && !loading;

  return (
    <Modal
      maskClosable
      width='auto'
      className={className}
      open={open}
      footer={[]}
      closable={false}
      onCancel={() => handlePressedAccessKey(false)}
    >
      <SearchInput
        autoFocus
        placeholder='Buscar por curso, autor(a) ou produtor(a)'
        onChange={handleSearch}
        ref={inputRef}
        suffix={null}
      />

      {shouldRenderEmptyState && (
        <EmptyState
          className='searchEmptyState'
          icon={<Search size={32} />}
          title='Digite no campo para iniciar uma busca.'
          description=''
        />
      )}

      <div className='listContainer'>
        {!shouldRenderEmptyState && (
          <InfiniteScroll className='list' onReachedEnd={() => handleReachedEnd()}>
            {(data?.data ?? []).map(item => (
              <div key={item.id} onClick={onClickResult(item.hash)} className='itemsList'>
                <Space align='center'>
                  <Display />
                  <Typography.Text className='itemText'>{item.title}</Typography.Text>
                </Space>
                <ArrowRightCircleFill />
              </div>
            ))}
            <div data-show={data?.total_pages === data?.page.page} className='loading'>
              <Typography.Text>Carregando</Typography.Text>
            </div>
          </InfiniteScroll>
        )}
      </div>
    </Modal>
  );
}

const Modal = styled(AntdModal)`
  ${({ theme }) => css`
    background: transparent;
    margin: auto;
    width: auto;
    display: flex;
    height: 100vh;
    top: 0;

    .ant-modal-content {
      background: transparent;
      width: 600px;
      margin: auto;
      box-shadow: none;
      box-sizing: content-box;
    }

    .listContainer {
      overflow-y: auto;
      background: ${theme.variables.bgElevated};

      .skeletons {
        padding: ${theme.space.spacing(4, 4, 0, 4)};
      }
    }

    .list {
      border-radius: 0 0 ${`${theme.borderRadius}px`} ${`${theme.borderRadius}px`};
      min-height: 500px;
      max-height: 500px;

      svg {
        margin: 14px;
        min-width: 14px;
        min-height: 14px;
      }

      & .itemText {
        font-size: 16px;
        font-weight: 500;
        color: ${theme.colorTextTertiary};
      }

      & .itemsList {
        justify-content: space-between;
        align-items: center;
        display: flex;
        margin-top: ${theme.space.spacing(1)};
        transition: background 0.2s;
        cursor: pointer;
        user-select: none;

        .ant-space-item {
          max-width: 470px;

          span {
            text-overflow: ellipsis;
            overflow: hidden;
            display: block;
            white-space: nowrap;
          }
        }
      }

      & .itemsList:hover {
        background: ${theme.variables.hoverBackground};
      }
    }

    .divider {
      margin: ${theme.space.spacing(3, 0, 0, 0)};
      background: ${theme.colorText};
    }

    .listRenderItem {
      padding: ${theme.space.spacing(4, 4, 0, 4)};
    }

    .loading {
      .ant-typography {
        display: block;
      }
      margin: 0 auto;
      height: 32px;
      width: fit-content;
      &[data-show='true'] {
        display: none;
      }
    }

    .searchEmptyState {
      padding: ${theme.space.spacing(8, 4, 8, 4)};
      background: ${theme.variables.bgElevated};
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: ${theme.space.spacing(3)};

      span {
        font-size: 16px;
      }
    }

    .ant-input-affix-wrapper {
      width: 100%;
      outline: none;
      box-shadow: ${theme.shadows[1]};
      z-index: 1;
      border-radius: ${`${theme.borderRadius}px`} ${`${theme.borderRadius}px`} 0 0;
    }

    .ant-input-affix-wrapper {
      border: 0;
    }
  `}
`;

export default memo(SearchModal);
